/**
 * Self-contained inline wrapper around CodeTablesTab (onboarding Step 2).
 *
 * CodeTablesTab is page-coupled on /product-sync: it borrows the page's shared
 * settings buffer and the sticky PageActionBar save/discard. This wrapper owns
 * its OWN buffer + coverage query and renders a local save/discard footer so
 * the full editor can live inside the onboarding wizard — the operator maps
 * codes and saves without leaving the page. Save goes through the same
 * `updateProductSyncSettings` mutation (via the buffer), so /product-sync
 * picks the result up on its next fetch.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { CodeTablesTab } from './CodeTablesTab';
import type { ProductSyncCoverage } from '../lib/types';
import { useProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';

export function CodeTablesEditorInline() {
  const { t } = useTranslation('productSync');
  const { isAdmin } = useRole();
  const admin = isAdmin();

  const buffer = useProductSyncSettingsBuffer();
  // BLU-791 (follow-up): this is the SECOND call site of the blocking one-shot
  // scan, on the onboarding route. `/product-sync` now streams via
  // `useCoverageScan`; this one still walks all 40 pages in a single request
  // and hangs Step 2 for a Threadheads-scale catalogue. Migrating it is its own
  // ticket — adapted here only so it keeps compiling against the folded shape.
  const coverageQuery = trpc.productSync.getProductSyncCoverage.useQuery();
  const coverage: ProductSyncCoverage | null = useMemo(() => {
    const raw = coverageQuery.data;
    if (!raw) return null;
    // `truncated` (hit a hard cap) -> `partial` (still incomplete). Same
    // consumer contract: do not present these numbers as exhaustive.
    const { truncated, ...rest } = raw;
    return { ...rest, partial: truncated };
  }, [coverageQuery.data]);

  // Same store-wide seen-option-names derivation as ProductSyncPage (coverage
  // signal only — no preview page here to fall back to).
  const seenOptionNames = useMemo(() => {
    const set = new Set<string>();
    const covSeen = (coverage as { seenOptionNames?: { colour?: string[]; size?: string[] } } | undefined)?.seenOptionNames;
    for (const n of covSeen?.colour ?? []) set.add(n.toLowerCase());
    for (const n of covSeen?.size ?? []) set.add(n.toLowerCase());
    return set;
  }, [coverage]);

  return (
    <div className="space-y-4">
      <CodeTablesTab
        coverage={coverage}
        // Onboarding keeps the old blocking behaviour until BLU-791: the scan
        // is this step's whole point and there is no tab to stream behind.
        bufferLoading={coverageQuery.isLoading || buffer.isLoading}
        coverageLoading={coverageQuery.isLoading}
        scannedProducts={coverage?.totals.products ?? 0}
        coverageComplete={coverage !== null && !coverage.partial}
        coverageScanning={coverageQuery.isFetching}
        coverageError={coverageQuery.error?.message ?? null}
        onRescan={() => void coverageQuery.refetch()}
        isError={coverageQuery.isError || buffer.isError}
        errorMessage={coverageQuery.error?.message ?? buffer.errorMessage}
        onRetry={() => {
          void coverageQuery.refetch();
          buffer.refetch();
        }}
        buffer={buffer}
        admin={admin}
        pendingFocus={null}
        onFocusConsumed={() => {}}
        seenOptionNames={seenOptionNames}
        // Phase 86 slice B: never part of onboarding.
        showDesignIdSource={false}
      />

      {/* Local save/discard footer — replaces the /product-sync sticky bar. */}
      {admin && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={buffer.discard}
            disabled={!buffer.hasChanges || buffer.isSaving}
            className="h-8 gap-1.5 px-3 text-xs"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            {t('actions.discard')}
          </Button>
          <Button
            size="sm"
            onClick={buffer.save}
            disabled={!buffer.hasChanges || buffer.isSaving}
            className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
          >
            {buffer.isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Save className="h-3.5 w-3.5" aria-hidden="true" />}
            {t('actions.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
